"use client";

import FadeIn from "@/components/ui/FadeIn";
import FeatureIllustration from "./FeatureIllustration";
import type { IllustrationKey } from "./FeatureIllustration";

export interface ZigzagFeature {
  overline: string;
  title: string;
  description: string;
  illustration: IllustrationKey;
}

export default function FeatureZigzag({
  features,
  // Heading level for each feature title. Default h3 (when the zigzag sits under
  // a real section h2, e.g. institutional-investing). Use-case pages pass "h2"
  // because their section has no descriptive h2 above the zigzag.
  headingLevel = "h3",
}: {
  features: ZigzagFeature[];
  headingLevel?: "h2" | "h3";
}) {
  const Heading = headingLevel;
  return (
    <div className="space-y-16 sm:space-y-24 lg:space-y-32">
      {features.map((feature, i) => {
        const isEven = i % 2 === 0;
        return (
          <div
            key={feature.title}
            className={`grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${
              isEven ? "" : "lg:[direction:rtl]"
            }`}
          >
            {/* Text side */}
            <div className={isEven ? "" : "lg:[direction:ltr]"}>
              <FadeIn delay={i * 0.05}>
                <p className="text-xs font-medium tracking-[0.3em] uppercase text-violet-800 mb-4">
                  {feature.overline}
                </p>
              </FadeIn>
              <FadeIn delay={i * 0.05 + 0.05}>
                <Heading className="text-2xl sm:text-3xl md:text-4xl font-[family-name:var(--font-display)] tracking-tight leading-[1.1] mb-4">
                  {feature.title}
                </Heading>
              </FadeIn>
              <FadeIn delay={i * 0.05 + 0.1}>
                <p className="text-base text-black/55 font-light leading-relaxed max-w-lg">
                  {feature.description}
                </p>
              </FadeIn>
            </div>

            {/* Illustration side */}
            <FadeIn
              delay={i * 0.05 + 0.15}
              direction={isEven ? "left" : "right"}
              className={isEven ? "" : "lg:[direction:ltr]"}
            >
              <FeatureIllustration name={feature.illustration} />
            </FadeIn>
          </div>
        );
      })}
    </div>
  );
}
